import create from 'zustand';
import { devtools } from 'zustand/middleware';
import { InventoryMeter, OutputTechnology, SerializedInventoryMeter } from '@enmon/commons/lib/tools';
import { pick } from 'rambda';
import { SerializedMeter } from '@enmon/commons/lib/enmon';
import { MeterFileValue } from '@enmon/commons/lib/enmon/files/values';
import { createInstallationDetailStepSlice, InstallationDetailStepSlice } from './steps/installation_detail/slice';
import { createPhotoStepSlice, PhotoStepSlice } from './steps/photo/slice';
import { createSensorScanStepSlice, SensorScanStepSlice } from './steps/sensor_scan/slice';
import { createSensorLoraStepSlice, SensorLoraStepSlice } from './steps/sensor_connection_check/slice';
import { createHardwaresStepSlice, HardwaresStepSlice } from './steps/hardwares/slice';
import {
  createInstallationTechnologyStepSlice,
  InstallationTechnologyStepSlice,
} from './steps/installation_technology/slice';
import { InstallMeterStep } from './enums/installMeterStep';
import {
  InstallMeterPhotosMap,
  InstalledMeterHardware,
  MeterInstallationData,
} from '../../services/api/inventoryMeters';
import { isThermometer } from '../../utils/isThermometer';

interface BaseStoreValues {
  isSendingData: boolean;
  queuedMetersCount?: number;
  inventoryMeter?: SerializedInventoryMeter;
  enmonMeter?: SerializedMeter;
  validSteps: Partial<Record<InstallMeterStep, boolean>>;
}

export type InstallMeterStore = BaseStoreValues &
  InstallationTechnologyStepSlice &
  InstallationDetailStepSlice &
  SensorScanStepSlice &
  HardwaresStepSlice &
  SensorLoraStepSlice &
  PhotoStepSlice & {
    setIsSendingData: (payload: boolean) => void;
    setQueuedMetersCount: (payload: number) => void;
    setInventoryMeter: (payload: SerializedInventoryMeter) => void;
    setEnmonMeter: (payload: SerializedMeter) => void;
    setIsStepValid: (step: InstallMeterStep, isValid: boolean) => void;
    reset: (memoizeValues?: boolean) => void;
    resetBaseValues: () => void;
  };

const INITIAL_STATE: BaseStoreValues = {
  isSendingData: false,
  queuedMetersCount: undefined,
  inventoryMeter: undefined,
  enmonMeter: undefined,
  validSteps: {},
};

/** Store for Install Meter module */
export const useStore = create<InstallMeterStore>(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      ...createInstallationTechnologyStepSlice(set),
      ...createSensorScanStepSlice(set),
      ...createSensorLoraStepSlice(set),
      ...createInstallationDetailStepSlice(set),
      ...createPhotoStepSlice(set),
      ...createHardwaresStepSlice(set),

      resetBaseValues: () => {
        set(INITIAL_STATE);
      },

      setIsSendingData: (payload: boolean) =>
        set(() => ({
          isSendingData: payload,
        })),

      setQueuedMetersCount: (count: number) => {
        set(() => ({ queuedMetersCount: count }));
      },

      setEnmonMeter: (payload: SerializedMeter) => {
        set(() => ({ enmonMeter: payload }));
      },

      setInventoryMeter: (payload: SerializedInventoryMeter) => {
        set(() => ({ inventoryMeter: payload }));
      },

      setIsStepValid: (step: InstallMeterStep, isValid: boolean) => {
        set(state => ({
          ...state,
          validSteps: {
            ...state.validSteps,
            [step]: isValid,
          },
        }));
      },

      reset: () => {
        get().resetBaseValues();
        get().resetInstallationTechnologyStepSlice();
        get().resetInstallationDetailStepSlice();
        get().resetSensorScanStepSlice();
        get().resetSensorLoraStepSlice();
        get().resetPhotoStepSlice();
        set(() => ({ inventoryMeter: undefined, enmonMeter: undefined }));
      },
    }),
    'InstallMeterStore',
  ),
);

/**
 * Selector for Form data object payload
 * @param {state} state state from main useStore.
 * @returns {SelectedInstallMeterFormData} FormData object.
 */
export const selectInstallMeterFormData =
  (installedBy: string | null) =>
  ({
    registers,
    registersCountersReadingAt,
    dataSourceId,
    hardwares,
    outputs,
    meterMountTechnology,
    enmonMeter,
  }: InstallMeterStore): MeterInstallationData => ({
    installed_at: new Date(),
    installed_by: installedBy,

    // Install reading
    registers: isThermometer(enmonMeter?.detail)
      ? []
      : registers.map(({ counterPhotos, factor, channel, ...register }) => ({
          ...register,
          ...(meterMountTechnology === OutputTechnology.Impulse && {
            channel: channel ?? null,
            factor: factor ?? null,
          }),
          counterPhotos: counterPhotos.reduce<File[]>((acc, uploadFile) => {
            if (uploadFile.originFileObj) {
              acc.push(uploadFile.originFileObj);
            }
            return acc;
          }, []),
        })),
    registers_counters_reading_at: registersCountersReadingAt,

    hardwares: hardwares.map(pick<keyof InstalledMeterHardware>(['id', 'serial_number'])),

    // Sensor scan step
    data_source_id: dataSourceId,

    // Registers step
    outputs,
    meter_mount_technology: meterMountTechnology,
  });

/**
 * Selector for Form Photos
 * @param {state} state state from main useStore.
 * @returns {SelectedInstallMeterPhotos} object with Form Files.
 */
export const selectInstallMeterFormPhotos = (state: InstallMeterStore): InstallMeterPhotosMap => {
  const photoTypes = Object.values(MeterFileValue).filter(
    (type: MeterFileValue): type is MeterFileValue =>
      [MeterFileValue.CablePath, MeterFileValue.OptoConnection].includes(type) === false,
  );

  return photoTypes.reduce<InstallMeterPhotosMap>(
    (acc, currentItem) => ({
      ...acc,
      [currentItem]: {
        fileList: state[currentItem]?.map(photo => photo.originFileObj) ?? [],
        photoContext: currentItem,
      },
    }),
    {},
  );
};

export interface InstallMeterRecord {
  formPhotos: InstallMeterPhotosMap;
  data: MeterInstallationData;
  inventoryMeterId: InventoryMeter['id'];
}
